import {
  createRouter, createRootRoute, createRoute, redirect, Outlet,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { AppLayout }      from '@/components/layout/app-layout';
import { Skeleton }       from '@/components/ui/skeleton';
import { useAppStore }    from '@/store/app.store';

// ── Lazy page imports ─────────────────────────────────────────────────────────
const LoginPage         = lazy(() => import('@/pages/auth/login'));
const RegisterPage      = lazy(() => import('@/pages/auth/register'));
const ForgotPage        = lazy(() => import('@/pages/auth/forgot-password'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password'));
const VerifyEmailPage   = lazy(() => import('@/pages/auth/verify-email'));
const OnboardingPage    = lazy(() => import('@/pages/onboarding'));
const DashboardPage     = lazy(() => import('@/pages/dashboard'));
const EmpresasPage      = lazy(() => import('@/pages/empresas'));
const DeclaracionesPage = lazy(() => import('@/pages/declaraciones'));
const ReporteGerencialPage = lazy(() => import('@/pages/declaraciones/reporte-gerencial'));
const PagosPage         = lazy(() => import('@/pages/pagos'));
const DetraccionesPage  = lazy(() => import('@/pages/detracciones'));
const RetencionesPge    = lazy(() => import('@/pages/retenciones'));
const PercepcionesPage  = lazy(() => import('@/pages/percepciones'));
const LiquidacionPage   = lazy(() => import('@/pages/liquidacion'));
const AfpNetPage        = lazy(() => import('@/pages/afp-net'));
const DeudasPage        = lazy(() => import('@/pages/deudas'));
const MultasPage        = lazy(() => import('@/pages/multas'));
const BuzonPage         = lazy(() => import('@/pages/buzon'));
const SirePage               = lazy(() => import('@/pages/sire'));
const SireConsolidadoPage    = lazy(() => import('@/pages/sire/consolidado'));
const CronogramaSunatPage    = lazy(() => import('@/pages/cronograma-sunat'));
const UitPage                = lazy(() => import('@/pages/uit'));
const CalculadoraPage   = lazy(() => import('@/pages/calculadora'));
const CalendarioPage    = lazy(() => import('@/pages/calendario'));
const ConfigPage             = lazy(() => import('@/pages/configuracion'));
const UsuariosPage           = lazy(() => import('@/pages/usuarios'));
const SistemaWorkerPage      = lazy(() => import('@/pages/sistema-worker'));
const WhatsappTemplatesPage  = lazy(() => import('@/pages/whatsapp-templates'));
const PaquetesPage           = lazy(() => import('@/pages/paquetes'));
const CobranzasPage          = lazy(() => import('@/pages/cobranzas'));
const EstudioPage            = lazy(() => import('@/pages/estudio'));
const ComunicacionesPage     = lazy(() => import('@/pages/comunicaciones'));

function PageFallback() {
  return <div className="p-8 space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-4 w-96" /><Skeleton className="h-64 w-full" /></div>;
}

function wrap(C: React.ComponentType) {
  return function WrappedPage() {
    return <Suspense fallback={<PageFallback />}><C /></Suspense>;
  };
}

// ── Routes ────────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute({ component: Outlet });

// Redirect / → proper page
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    const isAuth = !!useAppStore.getState().token;
    throw redirect({ to: isAuth ? '/dashboard' : '/auth/login' });
  },
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  beforeLoad: () => {
    const isAuth = !!useAppStore.getState().token;
    throw redirect({ to: isAuth ? '/dashboard' : '/auth/login' });
  },
});

// Auth group (no layout)
const authGroupRoute = createRoute({
  getParentRoute: () => rootRoute,
  id:             'auth-group',
  component:      Outlet,
});

const loginRoute        = createRoute({
  getParentRoute: () => authGroupRoute,
  path:           '/auth/login',
  // Si ya hay una sesión activa (token en el store), no debe poder llegarse
  // aquí (URL directa, "atrás" del navegador) sin pasar por logout() —
  // de lo contrario un login nuevo puede arrancar con tenant/empresa
  // heredados de la sesión anterior todavía en el store.
  beforeLoad: () => {
    if (useAppStore.getState().token) throw redirect({ to: '/dashboard' });
  },
  component: wrap(LoginPage),
});
const registerRoute     = createRoute({ getParentRoute: () => authGroupRoute, path: '/auth/register',        component: wrap(RegisterPage) });
const forgotRoute       = createRoute({ getParentRoute: () => authGroupRoute, path: '/auth/forgot-password', component: wrap(ForgotPage) });
const resetPassRoute    = createRoute({ getParentRoute: () => authGroupRoute, path: '/auth/reset-password',  component: wrap(ResetPasswordPage) });
const verifyEmailRoute  = createRoute({ getParentRoute: () => authGroupRoute, path: '/auth/verify-email',    component: wrap(VerifyEmailPage) });

// Onboarding (semi-protected)
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: () => {
    if (!useAppStore.getState().token) throw redirect({ to: '/auth/login' });
  },
  component: wrap(OnboardingPage),
});

// Protected app routes
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id:             'app',
  beforeLoad: ({ location }) => {
    if (!useAppStore.getState().token) {
      throw redirect({ to: '/auth/login', search: { redirect: location.pathname } });
    }
  },
  component: AppLayout,
});

const dashboardRoute     = createRoute({ getParentRoute: () => appRoute, path: '/dashboard',     component: wrap(DashboardPage) });
const empresasRoute      = createRoute({ getParentRoute: () => appRoute, path: '/empresas',      component: wrap(EmpresasPage) });
const declaracionesRoute = createRoute({ getParentRoute: () => appRoute, path: '/declaraciones', component: wrap(DeclaracionesPage) });
const reporteGerencialRoute = createRoute({ getParentRoute: () => appRoute, path: '/declaraciones/reporte-gerencial', component: wrap(ReporteGerencialPage) });
const pagosRoute         = createRoute({ getParentRoute: () => appRoute, path: '/pagos',         component: wrap(PagosPage) });
const detraccionesRoute  = createRoute({ getParentRoute: () => appRoute, path: '/detracciones',  component: wrap(DetraccionesPage) });
const retencionesRoute   = createRoute({ getParentRoute: () => appRoute, path: '/retenciones',   component: wrap(RetencionesPge) });
const percepcionesRoute  = createRoute({ getParentRoute: () => appRoute, path: '/percepciones',  component: wrap(PercepcionesPage) });
const liquidacionRoute   = createRoute({ getParentRoute: () => appRoute, path: '/liquidacion',   component: wrap(LiquidacionPage) });
const afpNetRoute        = createRoute({ getParentRoute: () => appRoute, path: '/afp-net',       component: wrap(AfpNetPage) });
const deudasRoute        = createRoute({ getParentRoute: () => appRoute, path: '/deudas',        component: wrap(DeudasPage) });
const multasRoute        = createRoute({ getParentRoute: () => appRoute, path: '/multas',        component: wrap(MultasPage) });
const buzonRoute         = createRoute({ getParentRoute: () => appRoute, path: '/buzon',         component: wrap(BuzonPage) });
const sireRoute          = createRoute({ getParentRoute: () => appRoute, path: '/sire',          component: wrap(SirePage) });
const sireConsolidadoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sire/consolidado',
  validateSearch: (s: Record<string, unknown>) => ({
    codLibro:        String(s.codLibro        ?? '140000'),
    perTributario:   String(s.perTributario   ?? ''),
    codTipoRegistro: String(s.codTipoRegistro ?? ''),
    label:           String(s.label           ?? ''),
  }),
  component: wrap(SireConsolidadoPage),
});
const cronogramaSunatRoute = createRoute({ getParentRoute: () => appRoute, path: '/cronograma-sunat', component: wrap(CronogramaSunatPage) });
const uitRoute             = createRoute({ getParentRoute: () => appRoute, path: '/uit',             component: wrap(UitPage) });
const calculadoraRoute   = createRoute({ getParentRoute: () => appRoute, path: '/calculadora',   component: wrap(CalculadoraPage) });
const calendarioRoute    = createRoute({ getParentRoute: () => appRoute, path: '/calendario',    component: wrap(CalendarioPage) });
const configRoute               = createRoute({ getParentRoute: () => appRoute, path: '/configuracion',      component: wrap(ConfigPage) });
const usuariosRoute             = createRoute({ getParentRoute: () => appRoute, path: '/usuarios',            component: wrap(UsuariosPage) });
const sistemaWorkerRoute        = createRoute({ getParentRoute: () => appRoute, path: '/sistema/worker',      component: wrap(SistemaWorkerPage) });
const whatsappTemplatesRoute    = createRoute({ getParentRoute: () => appRoute, path: '/whatsapp-templates',  component: wrap(WhatsappTemplatesPage) });
const paquetesRoute             = createRoute({ getParentRoute: () => appRoute, path: '/paquetes',            component: wrap(PaquetesPage) });
const cobranzasRoute            = createRoute({
  getParentRoute: () => appRoute,
  path:           '/cobranzas',
  validateSearch: (s: Record<string, unknown>) => ({ companyId: s.companyId ? String(s.companyId) : undefined }),
  component:      wrap(CobranzasPage),
});
const estudioRoute              = createRoute({ getParentRoute: () => appRoute, path: '/estudio',          component: wrap(EstudioPage) });
const comunicacionesRoute       = createRoute({ getParentRoute: () => appRoute, path: '/comunicaciones',   component: wrap(ComunicacionesPage) });

// ── Route tree ────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  indexRoute,
  homeRoute,
  authGroupRoute.addChildren([loginRoute, registerRoute, forgotRoute, resetPassRoute, verifyEmailRoute]),
  onboardingRoute,
  appRoute.addChildren([
    dashboardRoute, empresasRoute, declaracionesRoute, reporteGerencialRoute, pagosRoute,
    detraccionesRoute, retencionesRoute, percepcionesRoute, liquidacionRoute, afpNetRoute, deudasRoute, multasRoute,
    buzonRoute, sireRoute, sireConsolidadoRoute, cronogramaSunatRoute, uitRoute, calculadoraRoute, calendarioRoute, configRoute, usuariosRoute, sistemaWorkerRoute, whatsappTemplatesRoute, paquetesRoute, cobranzasRoute, estudioRoute, comunicacionesRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
